import React, { useState } from 'react';
import {
  Dialog,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Collapse,
  alpha,
  Avatar,
  Slide,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useFilteredNavigation } from '../../hooks/useFilteredNavigation';

interface MobileFullscreenMenuProps {
  open: boolean;
  onClose: () => void;
}

// Apple-style slide transition from left
const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="right" ref={ref} {...props} timeout={{ enter: 300, exit: 250 }} />;
});

export default function MobileFullscreenMenu({ open, onClose }: MobileFullscreenMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentOrganization } = useAuth();
  const { filteredNavigation } = useFilteredNavigation();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      // If clicking the already expanded section, close it
      if (prev[section]) {
        return {};
      }
      // Otherwise, close all others and open this one
      return { [section]: true };
    });
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={SlideTransition}
      disableScrollLock={true}
      disableEnforceFocus={true}
      disableAutoFocus={true}
      disableRestoreFocus={true}
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Menu
        </Typography>
        <IconButton onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* User Info */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: 'primary.main',
          }}
        >
          {user?.email?.[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={500} noWrap>
            {user?.email}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {currentOrganization?.name}
          </Typography>
        </Box>
      </Box>

      {/* Navigation List */}
      <List sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {filteredNavigation.map((item) => {
          if (item.standalone && item.path) {
            const isActive = location.pathname === item.path;
            return (
              <ListItemButton
                key={item.text}
                onClick={() => handleNavigation(item.path)}
                selected={isActive}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    bgcolor: alpha('#10b981', 0.1),
                    '& .MuiListItemIcon-root': {
                      color: 'primary.main',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'primary.main',
                      fontWeight: 600,
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 44 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: '1.0625rem',
                    fontWeight: isActive ? 600 : 500,
                  }}
                />
              </ListItemButton>
            );
          }

          if (item.children) {
            const isExpanded = expandedSections[item.text];
            const hasActiveChild = item.children.some(child =>
              location.pathname === child.path
            );

            return (
              <Box key={item.text}>
                {/* Section Header */}
                <ListItemButton
                  onClick={() => toggleSection(item.text)}
                  sx={{
                    mx: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    bgcolor: hasActiveChild ? alpha('#10b981', 0.05) : 'transparent',
                  }}
                >
                  <ListItemIcon sx={{ color: hasActiveChild ? 'primary.main' : 'text.secondary', minWidth: 44 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '1.0625rem',
                      fontWeight: hasActiveChild ? 600 : 500,
                      color: hasActiveChild ? 'primary.main' : 'text.primary',
                    }}
                  />
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>

                {/* Section Children */}
                <Collapse in={isExpanded} timeout={200}>
                  <List disablePadding>
                    {item.children.map(child => {
                      const isActive = location.pathname === child.path;
                      return (
                        <ListItemButton
                          key={child.text}
                          onClick={() => handleNavigation(child.path)}
                          selected={isActive}
                          sx={{
                            mx: 1,
                            mb: 0.5,
                            pl: 6,
                            borderRadius: 1,
                            '&.Mui-selected': {
                              bgcolor: alpha('#10b981', 0.1),
                              '& .MuiListItemIcon-root': {
                                color: 'primary.main',
                              },
                              '& .MuiListItemText-primary': {
                                color: 'primary.main',
                                fontWeight: 600,
                              },
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 40,
                              color: isActive ? 'primary.main' : 'text.secondary',
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.text}
                            primaryTypographyProps={{
                              fontSize: '1rem',
                              fontWeight: isActive ? 600 : 500,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
                <Divider sx={{ my: 1, mx: 2 }} />
              </Box>
            );
          }

          return null;
        })}
      </List>
    </Dialog>
  );
}
